/// First-visit cookie / storage consent banner.
///
/// ePrivacy + GDPR require *prior, opt-in* consent before anything
/// non-essential is stored or loaded. The banner sits at the
/// bottom of the screen until the user picks. Until then:
///   - Firebase Analytics (GA4) does NOT initialise.
///   - The lfh-web Beacon helpdesk loader does NOT inject.
///
/// Choices:
///   - "Accept all"     → analytics + helpdesk both load.
///   - "Essential only" → neither loads. The user can revisit the
///                        choice in Settings → Privacy.

import { useEffect, useState } from 'react'
import { getConsent, setConsent } from '../data/consent'

export function ConsentBanner() {
  const [visible, setVisible] = useState<boolean>(() => getConsent() === null)

  // If another tab grants consent, hide here too. (Same-tab grants
  // call setVisible directly so they don't need the listener.)
  useEffect(() => {
    if (!visible) return
    const onStorage = () => {
      if (getConsent() !== null) setVisible(false)
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [visible])

  if (!visible) return null

  return (
    <div
      role="dialog"
      aria-label="Cookie and storage consent"
      className="pointer-events-auto fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-slate-950/95 px-3 py-1.5 text-xs text-white/85 shadow-lg backdrop-blur"
    >
      <div className="mx-auto flex max-w-5xl flex-col items-stretch gap-2 sm:flex-row sm:items-center sm:justify-between">
        <p className="leading-snug">
          We use essential storage (sign-in, preferences). Optional analytics
          and helpdesk widget load only with consent.{' '}
          <span className="text-white/55">Change anytime in Settings → Privacy.</span>
        </p>
        <div className="flex shrink-0 gap-2">
          <button
            onClick={() => {
              setConsent('essential')
              setVisible(false)
            }}
            className="flex-1 rounded-md bg-slate-800 px-3 py-1 text-[11px] font-semibold text-white/80 ring-1 ring-white/15 hover:bg-slate-700 sm:flex-none"
          >
            Essential only
          </button>
          <button
            onClick={() => {
              setConsent('all')
              setVisible(false)
            }}
            className="flex-1 rounded-md bg-emerald-600 px-3 py-1 text-[11px] font-semibold text-white shadow hover:bg-emerald-500 sm:flex-none"
          >
            Accept all
          </button>
        </div>
      </div>
    </div>
  )
}
